import { defineStore } from 'pinia'
import { makeResetStoreAction } from '@/utils/helpers/pinia'

export const state = () => ({
  error: [],
  warning: [],
  success: [],
  info: [],
})

/**
 * @typedef {'error' | 'warning' | 'success' | 'info'} AlertType
 */

/**
 * @type {AlertType[]}
 */
const types = _.keys(state())

/**
 * Helper function to calculate timeout based on content length
 * @param {{ title?: string, content?: string | object | Array<object> }} options - Title and content to calculate timeout from
 * @returns {Number} Timeout in milliseconds
 */
function getTimeout ({ title = '', content = '' } = {}) {
  const charactersPerSecond = 25
  const chunks = _.chunk(`${title}.${JSON.stringify(content)}`.split(''), charactersPerSecond).length
  return (chunks * 1000) + 1500
}

/**
 * Helper function to order alerts by timestamp
 * @param {Object} alertObject - Object containing alert arrays
 * @returns {Array} Flattened and ordered array of alerts
 */
function getOrderedByTimestampAlerts (alertObject = {}) {
  return _(alertObject)
    .entries()
    .map(([type, alertList]) => alertList.map((alert) => ({ ...alert, type })))
    .flatten()
    .orderBy(['timestamp'], ['desc'])
    .value()
}

export const useAlertsStore = defineStore('alerts', {
  state,

  getters: {
    allAlertsOrderedByTimestamp: (state) => [
      ...getOrderedByTimestampAlerts({ error: state.error }),
      ...getOrderedByTimestampAlerts({
        warning: state.warning,
        success: state.success,
        info: state.info,
      }),
    ],
  },

  actions: {
    /**
     * Reset the alert state for a specific type or all types
     * @type {(key?: AlertType) => any}
     * @returns {any}
     */
    RESET_ALERT_STATE: makeResetStoreAction(state),

    /**
     * Show an alert with the specified parameters
     * @param {{
     *   type: AlertType,
     *   title?: String,
     *   content?: String | Object<String, any> | Array<Object>,
     *   keepOpen?: Boolean,
     *   dedupKey?: String,
     * }} options - Alert options. `dedupKey` silently suppresses subsequent
     *              alerts with the same key while the original is still in
     *              state.
     * @returns {Promise<Boolean>} `true` if the alert was suppressed
     *              (deduped or invalid), `false` if it was actually added.
     */
    async SHOW_ALERT ({ type, title, content, keepOpen = type === 'error', dedupKey }) {
      const timestamp = Date.now()
      const aborted = this.ADD_ALERT({ type, timestamp, title, content, dedupKey })

      if (keepOpen || aborted) return aborted

      await _.$pause(getTimeout({ title, content }))
      this.HIDE_ALERT({ type, timestamp })
      return false
    },

    /**
     * Hide a specific alert by type and timestamp
     * @param {{
     *   type: AlertType,
     *   timestamp: Number
     * }} options - Alert identifier containing type and timestamp
     * @returns {Promise<void>}
     */
    async HIDE_ALERT ({ type, timestamp }) {
      if (!types.includes(type)) return console.error(`HIDE_ALERT was passed an incorrect type`, { type })
      if (_.$isEmpty(timestamp)) return console.error(`HIDE_ALERT was passed an empty timestamp`, { timestamp })

      await _.$pause(300)
      this.REMOVE_ALERT({ type, timestamp })
    },

    /**
     * Add an alert to the state
     * @param {(
     *   {
     *     type: AlertType,
     *     timestamp: Number,
     *     title?: String,
     *     content?: String | Object<String, any> | Array<Object>,
     *     dedupKey?: String,
     *   }
     * )} [options={}]
     * @returns {Boolean} True if the alert was aborted, false otherwise
     */
    ADD_ALERT ({ type, timestamp, title, content, dedupKey }) {
      const missingRequiredFields = _.some([type, timestamp, title || content], _.$isEmpty)
      const problem = {
        missingRequiredFields: missingRequiredFields && {
          message: `Missing required fields: ${_.transform({ type, timestamp, title, content }, (acc, hasVal, key) => {
            if (hasVal) return
            acc.push(`\n- ${key}: ${hasVal}`)
          }, []).join('')}`,
        },
        duplicateByKey: dedupKey && _.find(this[type], { dedupKey }),
        duplicateAlert: _.find(this[type], { title, content }),
      }

      for (const aborted of _.values(problem)) {
        if (!aborted) continue
        if (aborted?.message) console.error(`[ADD_ALERT] Aborted: ${aborted.message}`)
        return true
      }

      this[type].push({ timestamp, title, content, ...(dedupKey && { dedupKey }) })
      return false
    },

    /**
     * Remove an alert from the state
     * @param {{
     *   type: AlertType,
     *   timestamp: Number
     * }} options - Alert identifier containing type and timestamp
     * @returns {void}
     */
    REMOVE_ALERT ({ type, timestamp }) {
      const index = _.findIndex(this[type], ['timestamp', timestamp])
      if (index >= 0) this[type].splice(index, 1)
    },
  },
})
